import React from "react";
import { copyText } from "../../../utils/copy-text";

/** Server-rendered first paint has no `window`; fall back to the prod app host. */
function appOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://app.mystrengthbook.com";
}

export function buildTourBookingUrl(token, { embed = false } = {}) {
  if (!token) return "";
  return `${appOrigin()}/book/consult/${encodeURIComponent(String(token))}${
    embed ? "?embed=1" : ""
  }`;
}

export function buildTourEmbedSnippet(token) {
  if (!token) return "";
  return `<iframe src="${buildTourBookingUrl(token, { embed: true })}" width="100%" height="500" style="border:0;max-width:1080px" title="Book a consult" loading="lazy"></iframe>`;
}

const TourShareLinkPanel = ({ tour, onChanged }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copied, setCopied] = React.useState(null);

  const tourId = String(tour?._id ?? tour?.id ?? "");
  const token = tour?.public_token ?? null;
  const enabled = Boolean(tour?.public_booking_enabled && token);

  const link = buildTourBookingUrl(token);
  const snippet = buildTourEmbedSnippet(token);

  React.useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async (what, text) => {
    const ok = await copyText(text);
    setCopied(ok ? what : null);
    setError(ok ? null : "Could not copy - select the text and copy manually.");
  };

  const request = async (url, options, label) => {
    if (!tourId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(url, { credentials: "same-origin", ...options });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? data.message ?? label);
      }
      onChanged?.();
    } catch (e) {
      setError(e?.message ?? label);
    } finally {
      setBusy(false);
    }
  };

  const shareLinkUrl = `/actions/scheduling/tours/${encodeURIComponent(tourId)}/share-link`;

  /** POST mints a fresh token, so it is reserved for a deliberate rotation. */
  const regenerate = () => {
    if (
      enabled &&
      !window.confirm(
        "Generate a new link? Anyone using the current link - including embeds already on your website - will stop being able to book."
      )
    ) {
      return;
    }
    request(shareLinkUrl, { method: "POST" }, "Failed to generate a new link");
  };

  /**
   * Re-enabling goes through PUT: POST .../share-link would mint a new token and
   * silently break every link and embed the gym has already shared.
   */
  const turnOn = () =>
    request(
      `/actions/scheduling/tours/${encodeURIComponent(tourId)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ public_booking_enabled: true }),
      },
      "Failed to turn the link on"
    );

  const turnOff = () =>
    request(shareLinkUrl, { method: "DELETE" }, "Failed to turn the link off");

  return (
    <div className="scheduling-tour-share">
      <div className="scheduling-tour-share-head">
        <h4 className="scheduling-tour-share-title">Public booking link</h4>
        <span
          className={
            enabled
              ? "scheduling-tour-share-state scheduling-tour-share-state--live"
              : "scheduling-tour-share-state scheduling-tour-share-state--off"
          }
        >
          {enabled ? "Live" : "Off"}
        </span>
      </div>

      {enabled ? (
        <>
          <label
            className="scheduling-tour-share-label"
            htmlFor={`tour-link-${tourId}`}
          >
            Share this link
          </label>
          <div className="scheduling-tour-share-row">
            <input
              id={`tour-link-${tourId}`}
              className="scheduling-tour-share-input"
              type="text"
              readOnly
              value={link}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              data-button-type="outline"
              className="scheduling-tour-share-copy"
              onClick={() => handleCopy("link", link)}
            >
              {copied === "link" ? "Copied" : "Copy"}
            </button>
          </div>

          <label
            className="scheduling-tour-share-label"
            htmlFor={`tour-embed-${tourId}`}
          >
            Or embed it on your website
          </label>
          <div className="scheduling-tour-share-row">
            <textarea
              id={`tour-embed-${tourId}`}
              className="scheduling-tour-share-input scheduling-tour-share-input--code"
              readOnly
              rows={3}
              value={snippet}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              data-button-type="outline"
              onClick={() => handleCopy("embed", snippet)}
            >
              {copied === "embed" ? "Copied" : "Copy"}
            </button>
          </div>

          <footer className="scheduling-tour-share-actions">
            <button
              type="button"
              data-button-type="tertiary"
              onClick={regenerate}
              disabled={busy}
            >
              Generate new link
            </button>
            <button
              type="button"
              data-button-type="tertiary"
              onClick={turnOff}
              disabled={busy}
            >
              Turn off booking
            </button>
          </footer>
        </>
      ) : (
        <>
          <p className="scheduling-tour-share-desc">
            Booking is turned off, so the link returns “no longer active”.
            Existing bookings are unaffected.
          </p>
          <button
            type="button"
            data-button-type="primary"
            className="scheduling-tour-share-enable"
            onClick={turnOn}
            disabled={busy}
          >
            {busy ? "Working…" : "Turn on booking"}
          </button>
        </>
      )}

      {error && (
        <p className="scheduling-tour-share-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default TourShareLinkPanel;
